import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import posthog from 'posthog-js';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';

const SURVEY_ANSWER_KEYS = [
    'onboarding.connect-profile.survey.answer.connect-later',
    'onboarding.connect-profile.survey.answer.just-exploring',
    'onboarding.connect-profile.survey.answer.no-access',
    'onboarding.connect-profile.survey.answer.not-safe',
] as const;

type SurveyAnswerKey = (typeof SURVEY_ANSWER_KEYS)[number];

interface ConnectProfileSkipSurveyProps {
    open: boolean;
    /**
     * What opened the survey. 'skip' means the user pressed Skip and is on their way out anyway;
     * 'exit' means it appeared unprompted, on a backdrop click. It rides on every event here so
     * the two populations can be told apart: until now a dismissal from either looked identical,
     * and both silently skipped the connect step.
     */
    trigger: 'skip' | 'exit' | null;
    /** `answered` is true only when the user picked an option and sent it, false on any dismissal. */
    onClose: (answered: boolean) => void;
}

const ConnectProfileSkipSurvey = ({ open, trigger, onClose }: ConnectProfileSkipSurveyProps) => {
    const { t } = useTranslation();
    const [selectedAnswer, setSelectedAnswer] = useState<SurveyAnswerKey | null>(null);

    useEffect(() => {
        if (open) {
            if (POSTHOG_ENABLED) posthog.capture('onboarding.connect-profile.survey-shown', { trigger });
            setSelectedAnswer(null);
        }
        // `trigger` is fixed for the life of one opening, so it does not belong in the deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleSubmit = () => {
        if (!selectedAnswer) return;
        if (POSTHOG_ENABLED) {
            posthog.capture('onboarding.connect-profile.survey-sent', {
                answer: selectedAnswer,
                trigger,
            });
        }
        onClose(true);
    };

    const handleDismiss = () => {
        if (POSTHOG_ENABLED) {
            posthog.capture('onboarding.connect-profile.survey-dismissed', { trigger });
        }
        onClose(false);
    };

    return (
        <Dialog open={open} onClose={handleDismiss} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Typography variant="h6">{t('onboarding.connect-profile.survey.title')}</Typography>
            </DialogTitle>
            <DialogContent>
                <Stack gap={1.5}>
                    <Typography variant="body2" color="textSecondary">
                        {t('onboarding.connect-profile.survey.subtitle')}
                    </Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                        {SURVEY_ANSWER_KEYS.map((answerKey) => (
                            <Chip
                                key={answerKey}
                                label={t(answerKey)}
                                onClick={() => setSelectedAnswer(answerKey)}
                                color={selectedAnswer === answerKey ? 'primary' : 'default'}
                                variant={selectedAnswer === answerKey ? 'filled' : 'outlined'}
                                clickable
                            />
                        ))}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="text"
                    color="secondary"
                    onClick={handleDismiss}
                    data-id="connect-skip-survey-dismiss"
                >
                    {t('onboarding.connect-profile.survey.dismiss')}
                </Button>
                <Button
                    variant="contained"
                    disabled={!selectedAnswer}
                    onClick={handleSubmit}
                    data-id="connect-skip-survey-send"
                >
                    {t('str.action.send')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConnectProfileSkipSurvey;
